// Попап «Настройки» из меню аккаунта: смена пароля, подтверждение почты и
// удаление аккаунта. Показывается только вошедшему — гостю в меню вместо
// этого пункта стоит кнопка входа.
import { useState, type ReactElement } from 'react';
import { Modal } from '../ui/shell';
import { ConfirmDialog } from '../ui/forms';
import { useI18n, useT } from '../i18n';
import { navigate } from '../router';
import { useAuth } from './AuthProvider';
import { NameChangeForm } from './NameChangeForm';
import { PasswordChangeForm } from './PasswordChangeForm';
import { deleteAccount, resendVerification } from './api';
import { errorKey } from './errors';
import styles from './AccountSettingsModal.module.css';

export interface AccountSettingsModalProps {
  onClose(): void;
}

export function AccountSettingsModal({ onClose }: AccountSettingsModalProps): ReactElement | null {
  const t = useT();
  const { lang } = useI18n();
  const { user, refresh } = useAuth();

  const [resent, setResent] = useState(false);
  const [resendBusy, setResendBusy] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Пункт «Настройки» гостю не показывают вовсе (см. AccountMenu), но модалка
  // теоретически может пережить выход из аккаунта, пока открыта — тогда молча
  // ничего не рисуем, а не падаем на user.email.
  if (!user) return null;

  async function handleResend(): Promise<void> {
    setResendBusy(true);
    await resendVerification(lang);
    setResendBusy(false);
    setResent(true);
  }

  async function handleDelete(): Promise<void> {
    setDeleteBusy(true);
    setDeleteError(null);
    const res = await deleteAccount();
    setDeleteBusy(false);
    if (!res.ok) {
      setDeleteError(errorKey(res.error));
      setConfirmOpen(false);
      return;
    }
    // Сессия уже закрыта сервером (см. комментарий deleteAccount в api.ts) —
    // остаётся перечитать себя, чтобы AuthProvider узнал о госте раньше, чем
    // о нём напомнит следующий /api/auth/me где-то ещё в приложении.
    await refresh();
    onClose();
    navigate('/');
  }

  return (
    <Modal title={t('account.menu.settings')} onClose={onClose}>
      <div className={styles.sections}>
        <section className={styles.section}>
          <NameChangeForm />
        </section>

        <section className={styles.section}>
          <h3 className={styles.legend}>{t('auth.password.change')}</h3>
          <PasswordChangeForm />
        </section>

        <section className={styles.section}>
          <h3 className={styles.legend}>{t('account.emailState')}</h3>
          <div className={styles.statusRow}>
            <span className={user.emailVerified ? styles.ok : styles.warn}>
              {t(user.emailVerified ? 'account.verified' : 'account.unverified')}
            </span>
            {!user.emailVerified &&
              (resent ? (
                <span className={styles.muted}>{t('auth.verify.sent')}</span>
              ) : (
                <button
                  type="button"
                  className={styles.linkBtn}
                  disabled={resendBusy}
                  onClick={() => void handleResend()}
                >
                  {t('auth.verify.resend')}
                </button>
              ))}
          </div>
        </section>

        <section className={`${styles.section} ${styles.danger}`}>
          <h3 className={styles.legend}>{t('account.delete.title')}</h3>
          <p className={styles.muted}>{t('account.delete.hint')}</p>
          {deleteError && (
            <p className={styles.error} role="alert">
              {t(deleteError)}
            </p>
          )}
          <button
            type="button"
            className="btn btn-danger"
            disabled={deleteBusy}
            onClick={() => setConfirmOpen(true)}
          >
            {t('account.delete.action')}
          </button>
        </section>
      </div>

      {/* ConfirmDialog (ui/forms) теперь рисует оверлей с z-index: 1200 — выше
          Modal (1000), так что вложенный диалог подтверждения виден поверх
          модалки как обычно, без дополнительных плясок со скрытием одного окна
          ради другого. */}
      <ConfirmDialog
        open={confirmOpen}
        title={t('account.delete.title')}
        message={t('account.delete.confirm')}
        confirmLabel={t('account.delete.action')}
        danger
        onConfirm={() => void handleDelete()}
        onCancel={() => setConfirmOpen(false)}
      />
    </Modal>
  );
}
